import {
    getMarkerTypeConfig,
    hasMarkerAvatarData,
    normalizeMarkerType,
    ICON_MODES,
} from './markerTypeConfig';
import { normalizeCommitmentDetails } from './commitment';

const DISPLAY_PLACEHOLDER = '—';
const MAX_METADATA_ITEMS = 6;

const EMAIL_VALUE_REGEX = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i;
const ISO_DATE_VALUE_REGEX = /^\d{4}[-./]\d{2}[-./]\d{2}(?:[T\s]\d{2}:\d{2}(?::\d{2})?(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?)?$/;
const PHONE_VALUE_REGEX = /^\+?[\d\s().-]+(?:\s*(?:ext\.?|x)\s*\d+)?$/i;
const MIN_PHONE_DIGITS = 7;

const SENSITIVE_METADATA_KEYS = [
    'email',
    'phone',
    'contact',
    'user',
    'uid',
    'guid',
    'token',
    'secret',
    'password',
    'address',
    'auth',
];

function isLikelyPhoneNumber(value) {
    if (!PHONE_VALUE_REGEX.test(value)) {
        return false;
    }
    if (ISO_DATE_VALUE_REGEX.test(value)) {
        return false;
    }
    return value.replace(/\D/g, '').length >= MIN_PHONE_DIGITS;
}

function sanitizeDisplayValue(value) {
    if (typeof value !== 'string') {
        return '';
    }
    return value.replace(/[\r\n\t]/g, ' ').replace(/\s+/g, ' ').trim();
}

function sanitizeArrayDisplayItem(value) {
    if (typeof value === 'string') {
        return sanitizeDisplayValue(value);
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
        return String(value);
    }
    if (value instanceof Date) {
        return sanitizeDisplayValue(value.toISOString());
    }
    return '';
}

function formatDisplayArrayValue(value) {
    if (!Array.isArray(value)) {
        return value;
    }
    return value
        .map(sanitizeArrayDisplayItem)
        .filter(Boolean)
        .join(', ');
}

function normalizeDateInput(value) {
    if (!value) {
        return null;
    }
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
}

export function extractMetadataEntries(metadata, priorityFields = [], options = {}) {
    if (!metadata || typeof metadata !== 'object') {
        return [];
    }

    const formatters = options?.formatters || {};
    const formatValue = (key, value) => (
        formatDisplayArrayValue(
            typeof formatters[key] === 'function' ? formatters[key](value) : value
        )
    );
    const isCommitmentValue = (value) => (
        value && typeof value === 'object' && value.kind === 'commitment'
    );
    const resolveCommitmentNotes = (value) => {
        if (!isCommitmentValue(value)) {
            return '';
        }
        const normalized = normalizeCommitmentDetails(value.raw);
        return typeof normalized?.notes === 'string' ? normalized.notes.trim() : '';
    };
    const isDisplayValue = (value) => (
        typeof value === 'string'
        || typeof value === 'number'
        || value instanceof Date
        || isCommitmentValue(value)
    );
    const isSensitiveEntry = (key, value, allowedKeys) => {
        if (allowedKeys && allowedKeys.has(key)) {
            return false;
        }
        if (typeof key === 'string') {
            const lowered = key.toLowerCase();
            if (SENSITIVE_METADATA_KEYS.some((sensitive) => lowered.includes(sensitive))) {
                return true;
            }
        }
        if (isCommitmentValue(value)) {
            const notes = resolveCommitmentNotes(value);
            if (!notes) {
                return false;
            }
            if (EMAIL_VALUE_REGEX.test(notes)) {
                return true;
            }
            return isLikelyPhoneNumber(notes);
        }
        if (typeof value === 'string') {
            const trimmed = value.trim();
            if (!trimmed) {
                return true;
            }
            if (EMAIL_VALUE_REGEX.test(trimmed)) {
                return true;
            }
            if (isLikelyPhoneNumber(trimmed)) {
                return true;
            }
        }
        if (typeof value === 'number') {
            if (!Number.isFinite(value)) {
                return true;
            }
            if (Number.isSafeInteger(value)) {
                return String(Math.abs(value)).length >= MIN_PHONE_DIGITS;
            }
        }
        return false;
    };

    const hideEmpty = options?.hideEmpty !== false;
    const prioritySet = new Set(priorityFields);

    const allEntries = Object.entries(metadata)
        .map(([key, value]) => [key, formatValue(key, value)])
        .map(([key, value]) => {
            if (!isDisplayValue(value) || (typeof value === 'string' && !value.trim())) {
                if (!hideEmpty && priorityFields.includes(key)) {
                    return [key, '—'];
                }
            }
            return [key, value];
        })
        .filter(([key, value]) => {
            if (!isDisplayValue(value)) {
                return false;
            }
            if (typeof value === 'string' && !value.trim()) {
                return false;
            }
            if (isSensitiveEntry(key, value, prioritySet)) {
                return false;
            }
            return true;
        });

    const priorityEntries = [];
    const otherEntries = [];

    allEntries.forEach(([key, value]) => {
        if (prioritySet.has(key)) {
            priorityEntries.push([key, value]);
        } else {
            otherEntries.push([key, value]);
        }
    });

    if (!hideEmpty) {
        const presentKeys = new Set(priorityEntries.map(([k]) => k));
        priorityFields.forEach((key) => {
            if (!presentKeys.has(key)) {
                priorityEntries.push([key, '—']);
            }
        });
    }

    priorityEntries.sort((a, b) => {
        const indexA = priorityFields.indexOf(a[0]);
        const indexB = priorityFields.indexOf(b[0]);
        return indexA - indexB;
    });

    if (options?.onlyPriority) {
        return priorityEntries.slice(0, MAX_METADATA_ITEMS);
    }

    return [...priorityEntries, ...otherEntries].slice(0, MAX_METADATA_ITEMS);
}

export function buildPopupModel(marker, {
    formatDateTime,
    authorLabel,
    createdLabel,
    visibilityLabel,
    visibilityValue,
    editAriaLabel = 'Edit marker',
    closeAriaLabel = 'Close marker details',
    unknownLabel = DISPLAY_PLACEHOLDER,
    canEdit = false,
    markerColor = null,
    metadataFormatters = {},
}) {
    const normalizedType = normalizeMarkerType(marker.type);
    const config = getMarkerTypeConfig(normalizedType);
    const typeLabel = config.label;
    const title = marker.name || typeLabel;
    const metadata = marker.metadata || {};
    const author = sanitizeDisplayValue(marker.authorName);
    const authorUsername = sanitizeDisplayValue(
        metadata.username
        || metadata.displayName
        || metadata.authorName
        || marker.authorName
    );

    const authorAvatar = config.extractAvatar(marker, metadata, author, authorUsername);
    const hasAuthorAvatarData = hasMarkerAvatarData(authorAvatar);
    const shouldShowAuthorAvatar = Boolean(hasAuthorAvatarData);
    const enableAvatarPreview = config.iconMode === ICON_MODES.AVATAR && Boolean(authorAvatar?.userId);

    const createdAt = normalizeDateInput(marker.createdAt);
    const formattedCreated = createdAt && typeof formatDateTime === 'function'
        ? sanitizeDisplayValue(formatDateTime(createdAt))
        : '';

    const popupOverrides = marker._popupOverrides;
    const effectivePriorityFields = popupOverrides?.priorityMetadataFields ?? config.priorityMetadataFields;
    const effectiveShowAuthor = popupOverrides ? popupOverrides.showAuthor : config.showAuthor;
    const effectiveShowVisibility = popupOverrides ? popupOverrides.showVisibility : config.showVisibility;
    const effectiveShowCreated = popupOverrides ? popupOverrides.showCreated : config.showCreated;

    const metadataEntries = extractMetadataEntries(metadata, effectivePriorityFields, {
        formatters: metadataFormatters,
        onlyPriority: Boolean(popupOverrides),
        hideEmpty: popupOverrides ? popupOverrides.hideEmpty : true,
    });

    return {
        id: marker.id,
        title,
        typeLabel,
        authorLabel,
        author: author || unknownLabel,
        createdLabel,
        created: formattedCreated || unknownLabel,
        visibilityLabel,
        visibility: visibilityValue || unknownLabel,
        metadataEntries,
        canEdit: Boolean(canEdit) && config.allowEdit,
        editAriaLabel,
        closeAriaLabel,
        unknownLabel,
        showAuthorAvatar: shouldShowAuthorAvatar,
        authorAvatar: hasAuthorAvatarData ? authorAvatar : null,
        enableAvatarPreview,
        markerColor,
        iconMode: config.iconMode,
        showAuthor: effectiveShowAuthor,
        showVisibility: effectiveShowVisibility,
        showCreated: effectiveShowCreated,
        marker,
    };
}
